import * as React from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/ui/button'
import { useIsAppContainerQuery } from '@/hooks/use-is-app-container-query'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/ui/dialog'

const DrawerModeCtx = React.createContext<{ isApp: boolean } | null>(null)
const useDrawerMode = () => {
  const ctx = React.useContext(DrawerModeCtx)
  if (!ctx) throw new Error('Drawer subcomponents must be used within <Drawer>')
  return ctx.isApp
}

type DrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root> & {
  allowDialogMode?: boolean
}

function Drawer({
  allowDialogMode = true,
  open,
  onOpenChange,
  children,
  ...props
}: DrawerProps) {
  const isAppQuery = useIsAppContainerQuery()
  const isApp = allowDialogMode ? isAppQuery : true

  return (
    <DrawerModeCtx.Provider value={{ isApp }}>
      {isApp ? (
        <DrawerPrimitive.Root
          data-slot="drawer"
          open={open}
          onOpenChange={onOpenChange}
          {...props}
        >
          {children}
        </DrawerPrimitive.Root>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          {children}
        </Dialog>
      )}
    </DrawerModeCtx.Provider>
  )
}

function DrawerTrigger(
  props: React.ComponentProps<typeof DrawerPrimitive.Trigger>,
) {
  const isApp = useDrawerMode()
  return isApp ? (
    <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />
  ) : (
    <DialogTrigger {...props} />
  )
}

function DrawerPortal(
  props: React.ComponentProps<typeof DrawerPrimitive.Portal>,
) {
  const isApp = useDrawerMode()
  return isApp ? (
    <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />
  ) : (
    <DialogPortal {...props} />
  )
}

function DrawerClose(
  props: React.ComponentProps<typeof DrawerPrimitive.Close>,
) {
  const isApp = useDrawerMode()
  return isApp ? (
    <DrawerPrimitive.Close data-slot="drawer-close" {...props} />
  ) : (
    <DialogClose {...props} />
  )
}

function DrawerCloseButton({
  onClose,
  className,
  iconColor = 'var(--color-black)',
}: Pick<React.ComponentProps<typeof DrawerPrimitive.Root>, 'onClose'> & {
  className?: string
  iconColor?: string
}) {
  return (
    <Button
      className={cn(
        'bg-gray-100 hover:bg-gray-100/70 absolute top-1.5 right-1.5 rounded-full transition-colors',
        'p-0 h-8 w-8 z-20',
        className,
      )}
      onClick={onClose}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="25"
        viewBox="0 0 25 25"
        fill="none"
      >
        <path
          d="M17.4479 7.80577C17.6014 7.95921 17.6876 8.16732 17.6876 8.38431C17.6876 8.60131 17.6014 8.80942 17.4479 8.96285L13.7838 12.627L17.4479 16.2911C17.6014 16.4445 17.6876 16.6526 17.6876 16.8696C17.6876 17.0866 17.6014 17.2947 17.4479 17.4481C17.2945 17.6016 17.0864 17.6878 16.8694 17.6878C16.6524 17.6878 16.4443 17.6016 16.2908 17.4481L12.6267 13.784L8.96264 17.4481C8.8092 17.6016 8.60109 17.6878 8.3841 17.6878C8.1671 17.6878 7.959 17.6016 7.80556 17.4481C7.65212 17.2947 7.56592 17.0866 7.56592 16.8696C7.56592 16.6526 7.65212 16.4445 7.80556 16.2911L11.4697 12.627L7.80556 8.96285C7.65212 8.80942 7.56592 8.60131 7.56592 8.38431C7.56592 8.16732 7.65212 7.95921 7.80556 7.80577C7.959 7.65233 8.1671 7.56613 8.3841 7.56613C8.60109 7.56613 8.8092 7.65233 8.96264 7.80577L12.6267 11.4699L16.2908 7.80577C16.4443 7.65233 16.6524 7.56613 16.8694 7.56613C17.0864 7.56613 17.2945 7.65233 17.4479 7.80577Z"
          fill={iconColor}
        />
      </svg>
    </Button>
  )
}

type DrawerContentProps = React.ComponentProps<
  typeof DrawerPrimitive.Content
> & {
  disableBottomStyles?: boolean
  showGrabber?: boolean
  showClose?: boolean
  fixedHeight?: boolean
  overlayClassName?: string
  drawerClassName?: string
  dialogClassName?: string
}

function DrawerContent({
  className,
  children,
  disableBottomStyles,
  showGrabber,
  showClose,
  fixedHeight,
  overlayClassName,
  drawerClassName,
  dialogClassName,
  ...props
}: DrawerContentProps) {
  const isApp = useDrawerMode()

  if (!isApp) {
    return (
      <DialogContent
        data-mode="dialog"
        className={cn('group', dialogClassName, className)}
        showCloseButton={showClose}
        {...props}
      >
        {children}
      </DialogContent>
    )
  }

  return (
    <DrawerPortal data-slot="drawer-portal">
      <DrawerPrimitive.Overlay
        data-slot="drawer-overlay"
        className={cn(
          overlayClassName ||
            cn(
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
              'backdrop-blur-[5px]',
            ),
        )}
      />
      <DrawerPrimitive.Content
        data-slot="drawer-content"
        data-mode="drawer"
        className={cn(
          'group',
          'group/drawer-content bg-background fixed z-50 flex h-auto flex-col p-6 pb-8',
          'data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80dvh] data-[vaul-drawer-direction=top]:rounded-b-[24px] data-[vaul-drawer-direction=top]:border-b',
          !disableBottomStyles &&
            'data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80dvh] data-[vaul-drawer-direction=bottom]:rounded-t-[24px] data-[vaul-drawer-direction=bottom]:border-t',
          'data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:sm:max-w-sm',
          'data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=left]:sm:max-w-sm',
          'outline-none ring-0 ring-offset-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
          fixedHeight && 'pt-safe-area inset-0',
          drawerClassName,
          className,
        )}
        {...props}
      >
        {showGrabber && (
          <div className="bg-gray-200 mx-auto mt-2 hidden h-1 w-[36px] shrink-0 rounded-full group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
        )}
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
}

function DrawerHeader(props: React.ComponentProps<'div'>) {
  const isApp = useDrawerMode()
  return isApp ? (
    <div
      data-slot="drawer-header"
      className={cn(
        'flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left',
        props.className,
      )}
      {...props}
    />
  ) : (
    <DialogHeader {...props} />
  )
}

function DrawerFooter(props: React.ComponentProps<'div'>) {
  const isApp = useDrawerMode()
  return isApp ? (
    <div
      data-slot="drawer-footer"
      className={cn('mt-auto flex flex-col gap-2 p-4', props.className)}
      {...props}
    />
  ) : (
    <DialogFooter {...props} />
  )
}

function DrawerTitle(
  props: React.ComponentProps<typeof DrawerPrimitive.Title>,
) {
  const isApp = useDrawerMode()
  return isApp ? (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn('text-foreground font-semibold', props.className)}
      {...props}
    />
  ) : (
    <DialogTitle {...props} />
  )
}

function DrawerDescription(
  props: React.ComponentProps<typeof DrawerPrimitive.Description>,
) {
  const isApp = useDrawerMode()
  return isApp ? (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn('text-muted-foreground text-x14', props.className)}
      {...props}
    />
  ) : (
    <DialogDescription {...props} />
  )
}

export {
  Drawer,
  DrawerPortal,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  DrawerCloseButton,
}
